<template>
  <section class="main">
    <div class="grid-container">
      <h2 class="u-extra-small-margin--bottom">{{ $t('weeks.projectplanningexport.title') }}</h2>

      <div class="grid-x grid-margin-x align-middle u-extra-small-margin--bottom">
        <div class="cell small-12 medium-2">
          <v-datepicker
            id="project-planning-export-date"
            class="form-control form-control--small form-control--date"
            :placeholder="$t('search.from')"
            :format="$t('search.from') + ' ' + $helpers.dateFormat"
            :disabled-dates="disabledDates"
            :language="de"
            :monday-first="true"
            v-model="selectedDate"
          />
        </div>
        <div class="cell small-12 medium-auto">
          <button
            class="button secondary"
            :disabled="isLoading || isSending || !selectedDate"
            v-on:click.prevent="sendToKiPython()"
          >{{ $t('weeks.projectplanningexport.send_button') }}</button>
          <button
            class="button secondary"
            :disabled="isLoading || isSending || !selectedDate"
            v-on:click.prevent="downloadExport()"
          >{{ $t('weeks.projectplanningexport.button') }}</button>
        </div>
       <div class="cell small-12 medium-auto u-text-right">
          <button
            class="button secondary"
            :disabled="isLoading || isSending"
            v-on:click.prevent="downloadHistory()"
          >{{ $t('weeks.projectplanningexport.history_button') }}</button>
        </div>
       <!--
        <div class="cell small-auto medium-6 u-text-right">
          <button
            class="button secondary"
            :title="$t('weeks.projectplanningexport.current_desc')"
            :disabled="isLoading || isSending"
            v-on:click.prevent="sendCurrentToKiPython()"
          >{{ $t('weeks.projectplanningexport.current_button') }}</button>
        </div>
        -->
      </div>

      <div class="text-center u-extra-small-margin--bottom u-extra-small-margin--top">
        <v-page-loading v-show="isLoading || isSending" />
        <p v-if="isSending">{{ $t('weeks.projectplanningexport.send_waiting', { seconds: sendElapsedSeconds }) }}</p>
      </div>

      <div v-if="!isLoading && summary" class="callout success">
        <p>{{ $t('weeks.projectplanningexport.success', { kolonnen: summary.kolonnen, projects: summary.projects }) }}</p>
      </div>

      <div v-if="!isLoading && historySummary" class="callout success">
        <p>{{ $t('weeks.projectplanningexport.history_success', { projects: historySummary.projects }) }}</p>
      </div>

      <div v-if="!isLoading && currentSummary" class="callout success">
        <p>{{ $t('weeks.projectplanningexport.current_success', { projects: currentSummary.projects }) }}</p>
      </div>

      <v-form-message type="error" id="form-error" :message="errorMessage" :autoCloseTimeout="0" />

      <!-- KI-Ergebnis: both weeks of the latest KI response -->
      <div v-if="hasAiResult" class="c-ai-result u-extra-large-margin--top">
        <div class="grid-x grid-margin-x align-middle u-extra-small-margin--bottom">
          <div class="cell small-auto">
            <h3 class="u-no-margin--bottom">{{ $t('weeks.projectplanningexport.map.title') }}</h3>
          </div>
          <div class="cell small-auto">
            <div class="c-ai-result__week-tools">
            <label class="c-ai-result__route-source" v-if="resultMode === 'week'">
              <span class="show-for-sr">{{ $t('weeks.projectplanningexport.map.route_source_label') }}</span>
              <select
                class="form-control form-control--small form-control--select"
                v-model="mapRouteSource"
                :disabled="isSending"
              >
                <option value="ki">{{ $t('weeks.projectplanningexport.map.route_ki') }}</option>
                <option value="original">{{ $t('weeks.projectplanningexport.map.route_original') }}</option>
              </select>
            </label>
            <div class="button-group u-text-right u-no-margin--bottom">
              <button
                v-for="tab in weekTabs"
                :key="tab.date"
                type="button"
                class="button"
                :class="tab.date === activeWeek ? 'primary' : 'secondary'"
                :disabled="isSending"
                v-on:click.prevent="selectWeekTab(tab.date)"
              >{{ tab.label }}</button>
            </div>
            </div>
          </div>
        </div>

        <div class="grid-x grid-margin-x">
          <div class="cell small-12 medium-1 c-ai-result__kolonnen">
            <h4>{{ $t('weeks.projectplanningexport.map.kolonne_label') }}</h4>
            <div class="button-group">
              <button
                v-for="kolonne in kolonneOptions"
                :key="kolonne"
                type="button"
                class="button u-extra-small-margin--bottom"
                :class="kolonne === selectedKolonne ? 'primary' : 'secondary'"
                :disabled="isSending"
                v-on:click.prevent="selectKolonne(kolonne)"
              >{{ kolonne }}</button>
            </div>
          </div>

            <div class="cell small-12 medium-11">
            <div class="c-ai-result__map">
              <ai-route-map :stops="mapStops" :start="mapStart" :draw-lines="showRouteLines" :empty-text="mapEmptyText" />
            </div>
            <!-- Legend begins -->
            <div class="grid-x grid-margin-x align-middle u-extra-small-margin--top">
                <div class="cell shrink">
                  <label class="c-ai-result__route-toggle">
                    <input type="checkbox" v-model="showRouteLines" />
                    {{ $t('weeks.projectplanningexport.map.show_routes') }}
                  </label>
                </div>
                <div class="cell auto u-text-right"><strong>{{ $t('weeks.projectplanningexport.map.legend_title') }}</strong></div>
                <div class="cell shrink" v-if="mapStart">
                    <span class="c-ai-result__legend-swatch c-ai-result__legend-swatch--start"></span>
                    S
                </div>
                <div class="cell shrink" v-for="day in weekdayLegend" :key="day">
                    <span class="c-ai-result__legend-swatch" :style="{ backgroundColor: weekdayColor(day) }"></span>
                    {{ weekdayShortLabel(day) }}.
                </div>
            </div>
            <!-- Legend ends -->
          </div>
        </div>
        
        <div class="grid-x grid-margin-x align-middle u-extra-large-margin--top u-extra-large-margin--bottom">
            <div class="cell small-1"></div>
            <div class="cell small-11">
                <div class="grid-x grid-margin-x u-extra-large-margin--bottom">
                    <div class="cell" :class="resultMode === 'week' ? 'small-6' : 'small-12'">
                       <h2 class="u-extra-small-margin--bottom">
                        {{ $t('weeks.projectplanningexport.map.stops_title') }}
                       </h2>
                        <p class="c-ai-result__complete">
                          <span>{{ $t('weeks.projectplanningexport.map.total_qm') }} {{ kiCompleteTotals.qmLabel }}</span>
                          <span>{{ $t('weeks.projectplanningexport.map.total_km') }} {{ kiCompleteTotals.kmLabel }}</span>
                        </p>
                    </div>
                    <div class="cell small-6" v-if="resultMode === 'week'">
                      <h2 class="u-extra-small-margin--bottom">
                        {{ $t('weeks.projectplanningexport.map.original_title') }}
                       </h2>
                        <p class="c-ai-result__complete">
                          <span>{{ $t('weeks.projectplanningexport.map.total_qm') }} {{ originalCompleteTotals.qmLabel }}</span>
                          <span>{{ $t('weeks.projectplanningexport.map.total_km') }} {{ originalCompleteTotals.kmLabel }}</span>
                        </p>
                    </div>
                </div>
                <div
                    v-for="group in kolonneOverviews"
                    :key="group.kolonne"
                    class="c-ai-result__kolonne-overview"
                >
                    <h4 class="u-extra-small-margin--bottom">
                        {{ $t('weeks.projectplanningexport.map.kolonne_label') }} {{ group.kolonne }}
                        <span v-if="String(group.kolonne) === String(selectedKolonne)" class="c-ai-result__kolonne-overview-note">
                            {{ $t('weeks.projectplanningexport.map.kolonne_on_map') }}
                        </span>
                    </h4>
                    <div class="grid-x grid-margin-x">
                        <div class="cell" :class="resultMode === 'week' ? 'small-6' : 'small-12'">
                            <small>{{ $t('weeks.projectplanningexport.map.stops_title') }}</small>
                        </div>
                        <div class="cell small-6" v-if="resultMode === 'week'">
                           <small>{{ $t('weeks.projectplanningexport.map.original_title') }}</small>
                        </div>
                    </div>

                    <div class="grid-x grid-margin-x">
                        <div class="cell" :class="resultMode === 'week' ? 'small-6' : 'small-12'">
                  
                            <p v-if="group.stops.length === 0">{{ $t('weeks.projectplanningexport.map.no_stops') }}</p>
                            <div v-if="group.kiStart" class="u-extra-small-margin--bottom callout">
                              {{ $t('weeks.projectplanningexport.map.start_note', { address: group.kiStart.addressLabel, km: group.kiStart.kmLabel }) }}
                            </div>
                            <table class="c-ai-result__stops" v-if="group.stops.length > 0">
                                <thead v-if="group.stops.length > 0">
                                    <tr>
                                        <th>{{ $t('weeks.projectplanningexport.map.stops_number') }}</th>
                                        <th>{{ $t('weeks.projectplanningexport.map.stops_day') }}</th>
                                        <th>{{ $t('weeks.projectplanningexport.map.stops_address') }}</th>
                                        <th>{{ $t('weeks.projectplanningexport.map.stops_leg_km') }}*</th>
                                        <th>{{ $t('weeks.projectplanningexport.map.stops_projects') }}</th>
                                        <th v-if="resultMode === 'current'">{{ $t('weeks.projectplanningexport.map.stops_open_qm') }}</th>
                                    </tr>
                                </thead>
                                <tbody v-if="group.stops.length > 0">
                                    <tr v-for="stop in group.stops" :key="group.kolonne + '-' + stop.key">
                                        <td>
                                            <span class="c-ai-result__legend-swatch" :style="{ backgroundColor: weekdayColor(stop.weekday) }"></span>
                                            {{ stop.number }}
                                        </td>
                                        <td>{{ stop.dayLabel }}</td>
                                        <td>{{ stop.addressLabel }}</td>
                                        <td class="u-text-right">{{ stop.legKmLabel }}</td>
                                        <td>
                                          <div v-for="project in stop.projects" :key="stop.key + '-' + project.projectId">
                                            {{ project.projectNumber }}
                                            <br v-if="project.dateFixed" />
                                            <small v-if="project.dateFixed" class="u-color--alert">{{ $t('weeks.project.is_date_fixed') }}</small>
                                          </div>
                                        </td>
                                        <td v-if="resultMode === 'current'">{{ stop.openQmLabel }}</td>
                                    </tr>
                                </tbody>
                            </table>
                        </div>
                        <div class="cell small-6" v-if="resultMode === 'week'">
                            <p v-if="weekplanExportLoading">{{ $t('weeks.projectplanningexport.map.original_loading') }}</p>
                            <p v-else-if="weekplanExportError">{{ weekplanExportError }}</p>
                            <p v-else-if="group.original.length === 0">{{ $t('weeks.projectplanningexport.map.original_empty') }}</p>
                            <div v-if="!weekplanExportLoading && !weekplanExportError && group.originalStart" class="callout u-extra-small-margin--bottom">
                              {{ $t('weeks.projectplanningexport.map.start_note', { address: group.originalStart.addressLabel, km: group.originalStart.kmLabel }) }}
                            </div>
                            <table class="c-ai-result__stops" v-if="!weekplanExportLoading && !weekplanExportError && group.original.length > 0">
                                <thead>
                                    <tr>
                                        <th>{{ $t('weeks.projectplanningexport.map.stops_number') }}</th>
                                        <th>{{ $t('weeks.projectplanningexport.map.stops_day') }}</th>
                                        <th>{{ $t('weeks.projectplanningexport.map.stops_address') }}</th>
                                        <th>{{ $t('weeks.projectplanningexport.map.stops_leg_km') }}*</th>
                                        <th>{{ $t('weeks.projectplanningexport.map.stops_projects') }}</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    <tr v-for="row in group.original" :key="group.kolonne + '-' + row.key">
                                        <td>
                                            <span class="c-ai-result__legend-swatch" :style="{ backgroundColor: weekdayColor(row.weekday) }"></span>
                                            {{ row.number }}
                                        </td>
                                        <td>{{ row.dayLabel }}</td>
                                        <td>{{ row.addressLabel }}</td>
                                        <td class="u-text-right">{{ row.legKmLabel }}</td>
                                        <td>
                                          {{ row.projectLabel }}
                                          <br v-if="row.dateFixed" />
                                          <small v-if="row.dateFixed" class="u-color--alert">{{ $t('weeks.project.is_date_fixed') }}</small>
                                        </td>
                                    </tr>
                                </tbody>
                            </table>
                        </div>
                    </div>
                </div>
                <div class="u-extra-small-margin--top" v-if="unplannedProjects.length > 0">
                    <h4 class="u-extra-small-margin--bottom">{{ $t('weeks.projectplanningexport.map.unplanned_title') }}</h4>
                    <p class="u-extra-small-margin--bottom">{{ $t('weeks.projectplanningexport.map.unplanned_desc') }}</p>
                    <div
                        v-for="project in unplannedProjects"
                        :key="project.project_id"
                        class="u-extra-small-margin--bottom"
                    >
                        <strong>{{ project.project_number }}</strong>
                        <p v-if="project.message" class="u-no-margin--bottom">
                            <strong>{{ $t('weeks.projectplanningexport.map.unplanned_reason') }}:</strong> {{ project.message }}
                        </p>
                    </div>
                </div>
          </div>
        </div>
        <div class="grid-x grid-margin-x">
          <div class="cell small-1"></div>
          <div class="cell small-11 medium-11 u-extra-small-margin--bottom">
            * {{ $t('weeks.projectplanningexport.map.km_disclaimer') }}
          </div>
        </div>
      </div>
    </div><!-- .grid-container-->
  </section>
</template>

<script>
import Datepicker from "vuejs-datepicker";
import { de } from "vuejs-datepicker/dist/locale";
import PageLoading from "./../partials/PageLoading";
import FormMessage from "./../partials/Forms/FormMessage";
import AiRouteMap from "./../partials/AiRouteMap";
import { WEEKDAY_MARKER_COLORS, WEEKDAY_LEGEND } from "./aiRouteMapColors";

export default {
  name: "ProjectPlanningExport",

  components: {
    vDatepicker: Datepicker,
    vPageLoading: PageLoading,
    vFormMessage: FormMessage,
    aiRouteMap: AiRouteMap
  },

  data: function() {
    return {
      de: de,
      // Only Mondays may be picked - every other weekday is disabled.
      disabledDates: {
        days: [0, 2, 3, 4, 5, 6]
      },
      selectedDate: this.getInitialDate(),
      isLoading: false,
      isSending: false,
      errorMessage: "",
      summary: null,
      historySummary: null,
      currentSummary: null,
      sendSuccessMessage: "",

      // "week" compares the KI plan with the stored weekplan. "current"
      // is the open/busy snapshot: only the KI plan is shown.
      resultMode: "week",
      // One KI response. It covers the requested Monday and the following
      // week; the tabs only filter that payload by planned_start.
      aiResult: null,
      // The week export for that same Monday, used to list the weekplan
      // entries already stored in the database.
      weekplanExport: null,
      weekplanExportLoading: false,
      weekplanExportError: "",
      // The Monday sent to KI. The two tabs are this week and the next one,
      // both taken from that single response.
      aiBaseDate: null,
      activeWeek: null,
      selectedKolonne: null,
      // Exactly one route on the map. "ki" is the kipython order, "original"
      // is the stored weekplan for the same kolonne and week.
      mapRouteSource: "ki",
      weekdayLegend: WEEKDAY_LEGEND,
      // Off until asked. Checking it is what starts the billed Routes call.
      showRouteLines: false,
      sendElapsedSeconds: 0,
      sendStartedAt: null,
      pollTimer: null,
      elapsedTimer: null,
      // Week tab to reopen after a bookmarked result has loaded.
      pendingView: null
    };
  },

  mounted: function() {
    // Restore a bookmarked/shared KI-Ergebnis view from the stored job.
    // A send that is still running resumes polling instead of starting
    // another request to kipython.
    const current = this.$route.query.mode === "current";
    const week =
      this.$route.query.week ||
      (current ? this.$moment().startOf("isoWeek").format("YYYY-MM-DD") : null);
    if (week) {
      this.resultMode = current ? "current" : "week";
      this.aiBaseDate = week;
      this.pendingView = this.$route.query.view || null;
      this.selectedKolonne = this.$route.query.kolonne || null;
      this.restoreJob(week, this.resultMode);
    }
  },

  beforeDestroy: function() {
    this.stopPolling();
  },

  watch: {
    // Keeps the datepicker in sync with whichever week is actually being
    // shown (initial ?week= restore, a KW tab click, or a fresh send), so
    // it never silently disagrees with the KI-Ergebnis panel/URL.
    activeWeek: function(newDate) {
      if (!newDate || this.resultMode === "current") {
        return;
      }

      const current = this.selectedDate ? this.$moment(this.selectedDate).format("YYYY-MM-DD") : null;
      if (current !== newDate) {
        this.selectedDate = this.$moment(newDate, "YYYY-MM-DD").toDate();
      }
    }
  },

  computed: {
    hasAiResult: function() {
      return this.aiResult !== null;
    },

    weekTabs: function() {
      if (!this.aiBaseDate) {
        return [];
      }

      return [0, 1].map(
        (function(offset) {
          const weekDate = this.$moment(this.aiBaseDate, "YYYY-MM-DD").add(offset * 7, "days");
          return {
            date: weekDate.format("YYYY-MM-DD"),
            label: "KW " + weekDate.isoWeek()
          };
        }).bind(this)
      );
    },

    // Every kolonne seen across all weeks fetched so far, so the buttons
    // stay put (rather than appearing/disappearing) when a kolonne simply
    // has nothing planned in the currently displayed week.
    kolonneOptions: function() {
      const names = {};
      ((this.aiResult && this.aiResult.routes) || []).forEach(function(route) {
        if (route && route.kolonne) {
          names[route.kolonne] = true;
        }
      });

      return Object.keys(names).sort();
    },

    activeWeekData: function() {
      return this.aiResult;
    },

    activeRoute: function() {
      if (!this.activeWeekData || !this.selectedKolonne) {
        return null;
      }
      return (
        (this.activeWeekData.routes || []).find(
          (function(route) {
            return String(route.kolonne) === String(this.selectedKolonne);
          }).bind(this)
        ) || null
      );
    },

    // Week one starts at kolonnen_start. A later week starts where this
    // kolonne finished week one, on whichever route the map is drawing.
    mapStart: function() {
      return this.startForMap(this.selectedKolonne);
    },

    // The one route on the map: KI order, or the stored weekplan. Never both.
    mapStops: function() {
      if (this.resultMode === "week" && this.mapRouteSource === "original") {
        return this.originalMapStops();
      }
      return this.stopsForRoute(this.activeRoute);
    },

    mapEmptyText: function() {
      if (this.resultMode === "week" && this.mapRouteSource === "original") {
        return this.$t("weeks.projectplanningexport.map.original_empty");
      }
      return this.$t("weeks.projectplanningexport.map.no_stops");
    },

    // Both weeks of every kolonne, from the kipython route metrics. These
    // do not change when a week tab is selected.
    kiCompleteTotals: function() {
      let qm = 0;
      let km = 0;
      let qmCount = 0;
      let kmCount = 0;

      ((this.aiResult && this.aiResult.routes) || []).forEach(function(route) {
        const metrics = route && route.metrics;
        if (!metrics) {
          return;
        }
        const totalQm = Number(metrics.total_qm);
        const distance = Number(metrics.distance);
        if (Number.isFinite(totalQm)) {
          qm += totalQm;
          qmCount += 1;
        }
        if (Number.isFinite(distance)) {
          km += distance;
          kmCount += 1;
        }
      });

      return {
        qmLabel: qmCount > 0 ? this.formatMetricQm(qm) : "—",
        kmLabel: kmCount > 0 ? this.formatMetricDistance(km, 2) : "—"
      };
    },

    // Both weeks of the stored weekplan, summed across every kolonne.
    // Kilometres use the same haversine formula as the KI distance.
    originalCompleteTotals: function() {
      let qm = 0;
      let qmCount = 0;
      let km = 0;
      let kmCount = 0;

      this.kolonneOptions.forEach(
        (function(kolonne) {
          const totalQm = this.originalQmForKolonne(kolonne);
          if (totalQm !== null) {
            qm += totalQm;
            qmCount += 1;
          }

          const distance = this.originalRouteKm(kolonne);
          if (distance !== null) {
            km += distance;
            kmCount += 1;
          }
        }).bind(this)
      );

      return {
        qmLabel: qmCount > 0 ? this.formatMetricQm(qm) : "—",
        kmLabel: kmCount > 0 ? this.formatMetricDistance(km, 2) : "—"
      };
    },

    // One pair of tables per kolonne for the displayed week. metrics is the
    // kipython summary for that kolonne's whole route.
    kolonneOverviews: function() {
      return this.kolonneOptions.map(
        (function(kolonne) {
          const route = ((this.aiResult && this.aiResult.routes) || []).find(function(item) {
            return item && String(item.kolonne) === String(kolonne);
          });
          const stops = this.decorateStops(this.stopsForRoute(route));
          const original = this.originalRowsForKolonne(kolonne);

          return {
            kolonne: kolonne,
            kiStart: this.startNote(kolonne, "ki", stops[0]),
            stops: stops,
            metrics: route && route.metrics ? route.metrics : null,
            originalStart: this.startNote(kolonne, "original", original[0]),
            original: original,
            originalQm: this.originalQmForKolonne(kolonne)
          };
        }).bind(this)
      );
    },

    // Not scoped to the selected kolonne - kipython reports these at the
    // week level, with no kolonne of their own (that's the whole reason
    // they're unplanned).
    unplannedProjects: function() {
      return (this.activeWeekData && this.activeWeekData.unplanned_projects) || [];
    },

  },

  methods: {
    // KI stops for one route in one week. Defaults to the displayed week.
    // A stop whose planned_end is a later day is one row per calendar day,
    // clipped to this week. Same address on the same day is one row.
    // Numbers continue across the week.
    stopsForRoute: function(route, weekDate) {
      const week = weekDate || this.activeWeek;
      if (!route || !Array.isArray(route.route) || !week) {
        return [];
      }

      const weekStart = this.$moment(week, "YYYY-MM-DD").startOf("day");
      const weekEnd = weekStart.clone().add(7, "days");
      const days = [];

      route.route.forEach(
        (function(stop) {
          if (!stop || !stop.planned_start || !stop.address) {
            return;
          }
          const lat = Number(stop.address.lat);
          const lon = Number(stop.address.lon);
          if (!Number.isFinite(lat) || !Number.isFinite(lon)) {
            return;
          }

          const start = this.$moment(stop.planned_start);
          if (!start.isValid()) {
            return;
          }
          let end = stop.planned_end ? this.$moment(stop.planned_end) : start.clone();
          if (!end.isValid() || end.isBefore(start)) {
            end = start.clone();
          }

          const lastDay = end.clone().startOf("day");
          const day = start.clone().startOf("day");
          while (!day.isAfter(lastDay)) {
            if (!day.isBefore(weekStart) && day.isBefore(weekEnd)) {
              days.push({
                stop: stop,
                day: day.clone(),
                lat: lat,
                lon: lon
              });
            }
            day.add(1, "day");
          }
        }).bind(this)
      );

      days.sort(function(a, b) {
        const byDay = a.day.format("YYYY-MM-DD").localeCompare(b.day.format("YYYY-MM-DD"));
        if (byDay !== 0) {
          return byDay;
        }
        const byStart = String(a.stop.planned_start).localeCompare(String(b.stop.planned_start));
        if (byStart !== 0) {
          return byStart;
        }
        return String(a.stop.planned_end || "").localeCompare(String(b.stop.planned_end || ""));
      });

      const groups = {};
      const order = [];

      days.forEach(
        (function(entry) {
          const day = entry.day.format("YYYY-MM-DD");
          const key = day + "|" + entry.lat + "|" + entry.lon;
          const stop = entry.stop;

          if (!groups[key]) {
            groups[key] = {
              key: key,
              lat: entry.lat,
              lon: entry.lon,
              day: day,
              weekday: entry.day.isoWeekday(),
              address: stop.address,
              plannedStart: stop.planned_start,
              plannedEnd: stop.planned_end,
              projects: []
            };
            order.push(key);
          }

          const projectId = stop.project_id;
          const alreadyListed = groups[key].projects.some(function(project) {
            return project.projectId === projectId;
          });
          if (!alreadyListed) {
            groups[key].projects.push({
              projectId: projectId,
              projectNumber: stop.project_number,
              openQm: this.openQmForProject(projectId)
            });
          }
        }).bind(this)
      );

      const origin = this.originBeforeWeek(route && route.kolonne, week, "ki");
      let previous = origin;

      return order.map(
        (function(key, index) {
          const stop = groups[key];
          const legKm = this.legKm(previous, stop);
          if (legKm !== null) {
            previous = stop;
          }

          return Object.assign({ number: index + 1, legKm: legKm }, stop);
        }).bind(this)
      );
    },

    decorateStops: function(stops) {
      return stops.map(
        (function(stop) {
          const address = stop.address || {};
          const street = [address.street, address.housenumber].filter(Boolean).join(" ");
          const city = [address.zipcode, address.city].filter(Boolean).join(" ");

          return {
            key: stop.key,
            number: stop.number,
            weekday: stop.weekday,
            dayLabel: this.$moment(stop.day, "YYYY-MM-DD").format("dd DD.MM."),
            addressLabel: [street, city].filter(Boolean).join(", "),
            projects: (stop.projects || [])
              .filter(function(project) {
                return project.projectNumber;
              })
              .map(
                (function(project) {
                  return {
                    projectId: project.projectId,
                    projectNumber: project.projectNumber,
                    dateFixed: this.projectIsDateFixed(project.projectId)
                  };
                }).bind(this)
              ),
            legKmLabel: this.legKmLabel(stop.legKm),
            openQmLabel: (stop.projects || [])
              .map(
                (function(project) {
                  return this.formatMetricQm(project.openQm);
                }).bind(this)
              )
              .join(", ")
          };
        }).bind(this)
      );
    },

    // Saved weekplan entries for one kolonne and one week. Defaults to the
    // displayed week. One database row can cover several days, so each
    // calendar day from startdate through enddate is its own row, in visit
    // order.
    originalEntryRows: function(kolonne, weekDate) {
      const week = weekDate || this.activeWeek;
      if (!this.weekplanExport || !kolonne || !week) {
        return [];
      }

      const weekStart = this.$moment(week, "YYYY-MM-DD").startOf("day");
      const weekEnd = weekStart.clone().add(7, "days");
      const kolonneName = String(kolonne);
      const rows = [];

      (this.weekplanExport.projects || []).forEach(
        (function(project) {
          (project.weekplan_entries || []).forEach(
            (function(entry) {
              if (!entry || String(entry.kolonne) !== kolonneName || !entry.startdate) {
                return;
              }
              const start = this.$moment(entry.startdate);
              if (!start.isValid()) {
                return;
              }
              let end = entry.enddate ? this.$moment(entry.enddate) : start.clone();
              if (!end.isValid() || end.isBefore(start)) {
                end = start.clone();
              }

              const lastDay = end.clone().startOf("day");
              const day = start.clone().startOf("day");
              while (!day.isAfter(lastDay)) {
                if (!day.isBefore(weekStart) && day.isBefore(weekEnd)) {
                  rows.push({
                    project: project,
                    entry: entry,
                    start: start.clone(),
                    end: end.clone(),
                    day: day.clone()
                  });
                }
                day.add(1, "day");
              }
            }).bind(this)
          );
        }).bind(this)
      );

      rows.sort(function(a, b) {
        const byDay = a.day.format("YYYY-MM-DD").localeCompare(b.day.format("YYYY-MM-DD"));
        if (byDay !== 0) {
          return byDay;
        }
        const byStart = String(a.entry.startdate).localeCompare(String(b.entry.startdate));
        if (byStart !== 0) {
          return byStart;
        }
        const byEnd = String(a.entry.enddate || "").localeCompare(String(b.entry.enddate || ""));
        if (byEnd !== 0) {
          return byEnd;
        }
        return (Number(a.entry.id) || 0) - (Number(b.entry.id) || 0);
      });

      return rows;
    },

    originalRowsForKolonne: function(kolonne) {
      let previous = this.originBeforeWeek(kolonne, this.activeWeek, "original");

      return this.originalEntryRows(kolonne).map(
        (function(row, index) {
          const address = row.project.address || {};
          const street = [address.street, address.housenumber].filter(Boolean).join(" ");
          const city = [address.zipcode, address.city].filter(Boolean).join(" ");
          const sameDay = row.start.format("YYYY-MM-DD") === row.end.format("YYYY-MM-DD");
          const timeLabel = sameDay
            ? row.start.format("HH:mm") + "–" + row.end.format("HH:mm")
            : row.start.format("DD.MM.") + "–" + row.end.format("DD.MM.");
          const here = {
            lat: Number(address.lat),
            lon: Number(address.lon)
          };
          const legKm = this.legKm(previous, here);
          if (legKm !== null) {
            previous = here;
          }

          return {
            key: String(row.entry.id) + "|" + row.day.format("YYYY-MM-DD"),
            number: index + 1,
            weekday: row.day.isoWeekday(),
            dayLabel: row.day.format("dd DD.MM."),
            timeLabel: timeLabel,
            addressLabel: [street, city].filter(Boolean).join(", "),
            projectLabel: row.project.project_number || "",
            dateFixed: !!(row.project && row.project.fixed_completion_date),
            legKmLabel: this.legKmLabel(legKm)
          };
        }).bind(this)
      );
    },

    // Sum of stats.qm.open for the projects on this kolonne across both
    // exported weeks. A project that appears on more than one day is counted once.
    originalQmForKolonne: function(kolonne) {
      const seen = {};
      let total = 0;
      let counted = 0;

      this.exportWeekDates().forEach(
        (function(week) {
          this.originalEntryRows(kolonne, week).forEach(function(row) {
            const project = row.project;
            if (!project || seen[project.project_id]) {
              return;
            }
            seen[project.project_id] = true;
            const qm = project.stats && project.stats.qm ? Number(project.stats.qm.open) : NaN;
            if (!Number.isFinite(qm)) {
              return;
            }
            total += qm;
            counted += 1;
          });
        }).bind(this)
      );

      return counted > 0 ? total : null;
    },

    // Remaining qm for one project on a current-export result. Null when
    // this result has no figure for that project.
    openQmForProject: function(projectId) {
      const map = (this.aiResult && this.aiResult.project_open_qm) || {};
      const value = Number(map[projectId]);
      return Number.isFinite(value) ? value : null;
    },

    // Address and kilometres from this table's route origin to its first stop.
    // The kilometres are the first row's existing leg, measured from that origin.
    startNote: function(kolonne, source, firstStop) {
      const origin = this.originBeforeWeek(kolonne, this.activeWeek, source);
      if (!origin || !firstStop) {
        return null;
      }

      const address = origin.address || {};
      const street = [address.street, address.housenumber].filter(Boolean).join(" ");
      const city = [address.zipcode, address.city].filter(Boolean).join(" ");

      return {
        addressLabel: [street, city].filter(Boolean).join(", ") || "—",
        kmLabel: firstStop.legKmLabel
      };
    },

    // kolonnen_start address for one kolonne. Null when that crew has no
    // usable coordinates, so the route begins at its first stop.
    startForKolonne: function(kolonne) {
      const starts = (this.aiResult && this.aiResult.kolonnen_start) || [];
      if (!Array.isArray(starts) || !kolonne) {
        return null;
      }

      const match = starts.find(function(item) {
        return item && String(item.kolonne) === String(kolonne);
      });
      if (!match || !match.address) {
        return null;
      }

      const lat = Number(match.address.lat);
      const lon = Number(match.address.lon);
      if (!Number.isFinite(lat) || !Number.isFinite(lon)) {
        return null;
      }

      return this.startPoint(kolonne, { lat: lat, lon: lon, address: match.address });
    },

    // Where this kolonne begins the given week. Week one starts at
    // kolonnen_start. A later week starts at the last stop of week one.
    originBeforeWeek: function(kolonne, week, source) {
      const isLater = !!(week && this.aiBaseDate && week !== this.aiBaseDate);
      if (!isLater) {
        return this.startForKolonne(kolonne);
      }

      const fromFirstWeek = source === "original"
        ? this.lastOriginalStop(kolonne, this.aiBaseDate)
        : this.lastKiStop(kolonne, this.aiBaseDate);

      return fromFirstWeek || this.startForKolonne(kolonne);
    },

    legKm: function(from, to) {
      if (!from || !to) {
        return null;
      }
      const fromLat = Number(from.lat);
      const fromLon = Number(from.lon);
      const toLat = Number(to.lat);
      const toLon = Number(to.lon);
      if (!Number.isFinite(fromLat) || !Number.isFinite(fromLon) || !Number.isFinite(toLat) || !Number.isFinite(toLon)) {
        return null;
      }

      return this.haversineKm(fromLat, fromLon, toLat, toLon);
    },

    legKmLabel: function(legKm) {
      const value = Number(legKm);
      if (legKm === null || legKm === undefined || !Number.isFinite(value)) {
        return "—";
      }

      return value.toLocaleString("de-DE", {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2
      });
    },

    // The second KW tab. Week one is aiBaseDate.
    isLaterWeek: function() {
      return !!(this.activeWeek && this.aiBaseDate && this.activeWeek !== this.aiBaseDate);
    },

    // Origin of the route on the map. A later week uses the last located
    // stop of week one on the same source as the stops. No such stop keeps
    // kolonnen_start: the crew never left that address.
    startForMap: function(kolonne) {
      if (!this.isLaterWeek()) {
        return this.startForKolonne(kolonne);
      }

      const fromFirstWeek =
        this.resultMode === "week" && this.mapRouteSource === "original"
          ? this.lastOriginalStop(kolonne, this.aiBaseDate)
          : this.lastKiStop(kolonne, this.aiBaseDate);

      return fromFirstWeek || this.startForKolonne(kolonne);
    },

    lastKiStop: function(kolonne, weekDate) {
      const route = ((this.aiResult && this.aiResult.routes) || []).find(function(item) {
        return item && String(item.kolonne) === String(kolonne);
      });
      const stops = this.stopsForRoute(route, weekDate);
      if (stops.length === 0) {
        return null;
      }

      return this.startPoint(kolonne, stops[stops.length - 1]);
    },

    lastOriginalStop: function(kolonne, weekDate) {
      const rows = this.originalEntryRows(kolonne, weekDate);
      for (let index = rows.length - 1; index >= 0; index -= 1) {
        const address = (rows[index].project && rows[index].project.address) || {};
        const lat = Number(address.lat);
        const lon = Number(address.lon);
        if (Number.isFinite(lat) && Number.isFinite(lon)) {
          return this.startPoint(kolonne, { lat: lat, lon: lon, address: address });
        }
      }

      return null;
    },

    startPoint: function(kolonne, stop) {
      return {
        key: "start|" + String(kolonne) + "|" + stop.lat + "|" + stop.lon,
        lat: stop.lat,
        lon: stop.lon,
        address: stop.address || {}
      };
    },

    // Map stops for the stored weekplan of the kolonne on the map. Same
    // visit order as the table. The same address on the same day is one
    // marker, and the number continues across the week.
    originalMapStops: function() {
      return this.originalMapStopsForKolonne(this.selectedKolonne);
    },

    originalMapStopsForKolonne: function(kolonne, weekDate) {
      const groups = {};
      const order = [];

      this.originalEntryRows(kolonne, weekDate).forEach(
        (function(row) {
          const address = (row.project && row.project.address) || {};
          const lat = Number(address.lat);
          const lon = Number(address.lon);
          if (!Number.isFinite(lat) || !Number.isFinite(lon)) {
            return;
          }

          const day = row.day.format("YYYY-MM-DD");
          const key = day + "|" + lat + "|" + lon;
          if (!groups[key]) {
            groups[key] = {
              key: key,
              lat: lat,
              lon: lon,
              day: day,
              weekday: row.day.isoWeekday(),
              address: address,
              plannedStart: day,
              plannedEnd: null,
              projects: []
            };
            order.push(key);
          }

          const projectId = row.project.project_id;
          const projectNumber = row.project.project_number;
          const alreadyListed = groups[key].projects.some(function(project) {
            return project.projectId === projectId;
          });
          if (!alreadyListed) {
            groups[key].projects.push({
              projectId: projectId,
              projectNumber: projectNumber
            });
          }
        }).bind(this)
      );

      return order.map(function(key, index) {
        return Object.assign({ number: index + 1 }, groups[key]);
      });
    },

    // The Bavarian public holidays already cached in the store (for the
    // weekly calendar, see the holidays store module) that fall inside the
    // two-week export window starting on the given Monday. Passed to the
    // backend so it can echo them back as `dates_to_skip` for the KI
    // planner to treat as non-working days.
    holidayDatesForWindow: function(mondayDate) {
      const start = this.$moment(mondayDate, "YYYY-MM-DD").startOf("day");
      const end = this.$moment(mondayDate, "YYYY-MM-DD")
        .add(11, "days")
        .endOf("day");
      const holidays = this.$store.getters.getHolidays || {};

      return Object.keys(holidays)
        .map(function(name) {
          return holidays[name] && holidays[name].datum ? holidays[name].datum : null;
        })
        .filter(
          (function(datum) {
            return (
              datum &&
              this.$moment(datum, "YYYY-MM-DD").isBetween(start, end, "day", "[]")
            );
          }).bind(this)
        )
        .sort();
    },

    getInitialDate: function() {
      // ?week= (the active KI-Ergebnis week, kept in the URL by
      // updateRouteQuery()) takes priority over the :date path param so a
      // shared/bookmarked link reopens with the datepicker matching what
      // the map actually shows, not a stale route param.
      const date = this.$route.query.week || this.$route.params.date;
      if (date) {
        return this.$moment(date, "YYYY-MM-DD")
          .startOf("week")
          .toDate();
      }
      return this.$moment()
        .startOf("week")
        .toDate();
    },

    downloadExport: function() {
      if (!this.selectedDate) {
        return;
      }

      this.isLoading = true;
      this.errorMessage = "";
      this.summary = null;
      this.historySummary = null;
      this.currentSummary = null;
      this.sendSuccessMessage = "";

      const date = this.$moment(this.selectedDate).format("YYYY-MM-DD");

      let promise = this.$api
        .clearParams()
        .addParam("date", date)
        .addParam("holidays", this.holidayDatesForWindow(date).join(","))
        .get("weekplan/project-planning-export");

      promise
        .then(
          (response => {
            const data = response.data.data;
            this.summary = {
              kolonnen: data.kolonnen.length,
              projects: data.projects.length
            };
            this.triggerDownload(data, date, "project-planning-export");
          }).bind(this)
        )
        .catch(
          (error => {
            this.errorMessage =
              (error.response &&
                error.response.data &&
                error.response.data.message) ||
              this.$t('weeks.projectplanningexport.error');
          }).bind(this)
        )
        .finally(
          (() => {
            this.isLoading = false;
          }).bind(this)
        );
    },

    // Projects only, from 1 January three years ago through today.
    downloadHistory: function() {
      this.isLoading = true;
      this.errorMessage = "";
      this.summary = null;
      this.historySummary = null;
      this.currentSummary = null;
      this.sendSuccessMessage = "";

      const date = this.$moment().format("YYYY-MM-DD");

      this.$api
        .clearParams()
        .get("weekplan/project-planning-history-export")
        .then(
          (response => {
            const projects = response.data.data.projects;
            this.historySummary = {
              projects: projects.length
            };
            this.triggerDownload(
              { projects: projects },
              date,
              "project-planning-history-export"
            );
          }).bind(this)
        )
        .catch(
          (error => {
            this.errorMessage =
              (error.response &&
                error.response.data &&
                error.response.data.message) ||
              this.$t("weeks.projectplanningexport.history_error");
          }).bind(this)
        )
        .finally(
          (() => {
            this.isLoading = false;
          }).bind(this)
        );
    },

    // A project is date-fixed when the loaded week export recorded a
    // fixed completion date from one of its weekplan entries.
    projectIsDateFixed: function(projectId) {
      const projects = (this.weekplanExport && this.weekplanExport.projects) || [];
      const match = projects.find(function(project) {
        return project && String(project.project_id) === String(projectId);
      });

      return !!(match && match.fixed_completion_date);
    },

    // Sends every currently open/busy project to kipython. The datepicker
    // is ignored: the anchor is this week's Monday, and the map shows only
    // the plan kipython returns.
    sendCurrentToKiPython: function() {
      const weekStart = this.$moment()
        .startOf("isoWeek")
        .format("YYYY-MM-DD");
      this.aiBaseDate = weekStart;
      this.mapRouteSource = "ki";
      this.startKiJob("current", "weekplan/project-planning-current-export/send", {
        holidays: this.holidayDatesForWindow(weekStart)
      });
    },

    sendToKiPython: function() {
      if (!this.selectedDate) {
        return;
      }

      const date = this.$moment(this.selectedDate).format("YYYY-MM-DD");
      this.aiBaseDate = date;
      this.startKiJob("week", "weekplan/project-planning-export/send", {
        date: date,
        holidays: this.holidayDatesForWindow(date)
      });
    },

    selectWeekTab: function(date) {
      if (this.isSending) {
        return;
      }

      this.activeWeek = date;
      this.updateRouteQuery();
    },

    selectKolonne: function(kolonne) {
      this.selectedKolonne = kolonne;
      this.updateRouteQuery();
    },

    // Starts a KI request. mode "week" is the datepicker comparison.
    // mode "current" is the open/busy snapshot, with no original plan.
    startKiJob: function(mode, path, body) {
      this.stopPolling();
      this.resultMode = mode;
      this.isSending = true;
      this.isLoading = false;
      this.summary = null;
      this.historySummary = null;
      this.currentSummary = null;
      this.errorMessage = "";
      this.sendSuccessMessage = "";
      this.aiResult = null;
      this.weekplanExport = null;
      this.weekplanExportError = "";
      this.weekplanExportLoading = false;

      return this.$api
        .clearParams()
        .post(path, body)
        .then(
          (response => {
            if (this._isDestroyed) {
              return;
            }
            this.handleJob(response.data.data);
          }).bind(this)
        )
        .catch(
          (error => {
            if (this._isDestroyed) {
              return;
            }
            this.isSending = false;
            this.stopPolling();
            this.errorMessage =
              (error.response &&
                error.response.data &&
                error.response.data.message) ||
              this.$t('weeks.projectplanningexport.map.week_error');
          }).bind(this)
        );
    },

    restoreJob: function(date, mode) {
      let request = this.$api.clearParams().addParam("date", date);
      if (mode === "current") {
        request = request.addParam("source", "current");
      }
      request
        .get("weekplan/project-planning-export/send")
        .then(
          (response => {
            if (this._isDestroyed) {
              return;
            }
            this.handleJob(response.data.data);
          }).bind(this)
        )
        .catch(
          (error => {
            if (this._isDestroyed) {
              return;
            }
            // A shared link with no stored job yet is not an error.
            if (error.response && error.response.status === 404) {
              return;
            }
            this.errorMessage =
              (error.response &&
                error.response.data &&
                error.response.data.message) ||
              this.$t('weeks.projectplanningexport.map.week_error');
          }).bind(this)
        );
    },

    handleJob: function(job) {
      if (!job) {
        this.isSending = false;
        this.stopPolling();
        return;
      }

      if (job.source === "current" || job.source === "week") {
        this.resultMode = job.source;
      }

      if (job.status === "succeeded") {
        if (job.result) {
          this.applyWeekResult(job.date, job.result);
        } else {
          this.errorMessage = job.error || this.$t('weeks.projectplanningexport.map.week_error');
        }
        this.isSending = false;
        this.stopPolling();
        return;
      }

      if (job.status === "failed") {
        this.isSending = false;
        this.stopPolling();
        this.errorMessage = job.error || this.$t('weeks.projectplanningexport.map.week_error');
        return;
      }

      this.isSending = true;
      this.startElapsed(job.created_on);
      this.schedulePoll(job.id);
    },

    applyWeekResult: function(date, data) {
      this.aiResult = data;
      this.aiBaseDate = date;
      const nextWeek = this.$moment(date, "YYYY-MM-DD")
        .add(7, "days")
        .format("YYYY-MM-DD");
      this.activeWeek = this.pendingView === nextWeek ? nextWeek : date;
      this.pendingView = null;

      if (!this.selectedKolonne || this.kolonneOptions.indexOf(this.selectedKolonne) === -1) {
        this.selectedKolonne = this.kolonneOptions[0] || null;
      }

      this.sendSuccessMessage = this.$t('weeks.projectplanningexport.send_success');
      this.updateRouteQuery();
      if (this.resultMode === "week") {
        this.loadWeekplanExport(date);
        return;
      }

      this.weekplanExport = null;
      this.weekplanExportError = "";
      this.weekplanExportLoading = false;
    },

    // Loads the week export for the Monday that was sent to KI. Its
    // weekplan_entries are the stored plan, shown beside the KI order.
    loadWeekplanExport: function(date) {
      const requestId = (this.weekplanExportRequest || 0) + 1;
      this.weekplanExportRequest = requestId;
      this.weekplanExport = null;
      this.weekplanExportError = "";
      this.weekplanExportLoading = true;

      this.$api
        .clearParams()
        .addParam("date", date)
        .addParam("holidays", this.holidayDatesForWindow(date).join(","))
        .get("weekplan/project-planning-export")
        .then(
          (response => {
            if (this._isDestroyed || this.weekplanExportRequest !== requestId) {
              return;
            }
            this.weekplanExport = response.data.data;
          }).bind(this)
        )
        .catch(
          (error => {
            if (this._isDestroyed || this.weekplanExportRequest !== requestId) {
              return;
            }
            this.weekplanExportError =
              (error.response &&
                error.response.data &&
                error.response.data.message) ||
              this.$t("weeks.projectplanningexport.map.original_error");
          }).bind(this)
        )
        .finally(
          (() => {
            if (this._isDestroyed || this.weekplanExportRequest !== requestId) {
              return;
            }
            this.weekplanExportLoading = false;
          }).bind(this)
        );
    },

    schedulePoll: function(jobId) {
      if (this.pollTimer !== null) {
        clearTimeout(this.pollTimer);
      }

      this.pollTimer = setTimeout(
        (function() {
          this.pollTimer = null;
          this.pollJob(jobId);
        }).bind(this),
        3000
      );
    },

    pollJob: function(jobId) {
      this.$api
        .clearParams()
        .get("weekplan/project-planning-export/send/" + jobId)
        .then(
          (response => {
            if (this._isDestroyed) {
              return;
            }
            this.handleJob(response.data.data);
          }).bind(this)
        )
        .catch(
          (error => {
            if (this._isDestroyed) {
              return;
            }
            this.isSending = false;
            this.stopPolling();
            this.errorMessage =
              (error.response &&
                error.response.data &&
                error.response.data.message) ||
              this.$t('weeks.projectplanningexport.map.week_error');
          }).bind(this)
        );
    },

    startElapsed: function(createdOn) {
      if (this.sendStartedAt === null) {
        const started = createdOn ? this.$moment(createdOn).valueOf() : Date.now();
        this.sendStartedAt = Number.isFinite(started) ? started : Date.now();
        this.sendElapsedSeconds = Math.max(0, Math.floor((Date.now() - this.sendStartedAt) / 1000));
      }

      if (this.elapsedTimer !== null) {
        return;
      }

      this.elapsedTimer = setInterval(
        (function() {
          this.sendElapsedSeconds = Math.max(0, Math.floor((Date.now() - this.sendStartedAt) / 1000));
        }).bind(this),
        1000
      );
    },

    stopPolling: function() {
      if (this.pollTimer !== null) {
        clearTimeout(this.pollTimer);
        this.pollTimer = null;
      }
      if (this.elapsedTimer !== null) {
        clearInterval(this.elapsedTimer);
        this.elapsedTimer = null;
      }
      this.sendStartedAt = null;
      this.sendElapsedSeconds = 0;
    },

    updateRouteQuery: function() {
      if (!this.activeWeek) {
        return;
      }

      const query = Object.assign({}, this.$route.query);
      if (this.resultMode === "current") {
        query.mode = "current";
      } else {
        delete query.mode;
      }
      query.week = this.aiBaseDate || this.activeWeek;
      if (this.activeWeek && this.activeWeek !== query.week) {
        query.view = this.activeWeek;
      } else {
        delete query.view;
      }
      if (this.selectedKolonne) {
        query.kolonne = this.selectedKolonne;
      } else {
        delete query.kolonne;
      }

      this.$router.replace({ query: query }).catch(function() {
        // vue-router rejects navigating to the same route/query - fine to ignore here.
      });
    },

    // The two Mondays covered by one export. Week one is aiBaseDate.
    exportWeekDates: function() {
      if (!this.aiBaseDate) {
        return this.activeWeek ? [this.activeWeek] : [];
      }

      return [0, 1].map(
        (function(offset) {
          return this.$moment(this.aiBaseDate, "YYYY-MM-DD")
            .add(offset * 7, "days")
            .format("YYYY-MM-DD");
        }).bind(this)
      );
    },

    // kolonnen_start, then every stored entry row of both weeks in visit order.
    // One point per row, so a revisited address counts as a leg, matching the table.
    originalRoutePoints: function(kolonne) {
      const points = [];
      const start = this.startForKolonne(kolonne);
      if (start) {
        points.push(start);
      }
      this.exportWeekDates().forEach(
        (function(week) {
          this.originalEntryRows(kolonne, week).forEach(function(row) {
            const address = (row.project && row.project.address) || {};
            const lat = Number(address.lat);
            const lon = Number(address.lon);
            if (Number.isFinite(lat) && Number.isFinite(lon)) {
              points.push({ lat: lat, lon: lon });
            }
          });
        }).bind(this)
      );
      return points;
    },

    // Straight-line kilometres between two coordinates, multiplied by the
    // same 1.3 road factor the KI distance uses.
    haversineKm: function(lat1, lon1, lat2, lon2) {
      const earthRadiusKm = 6371;
      const roadFactor = 1.3;
      const toRadians = function(degrees) {
        return degrees * Math.PI / 180;
      };
      const phi1 = toRadians(lat1);
      const phi2 = toRadians(lat2);
      const dPhi = toRadians(lat2 - lat1);
      const dLambda = toRadians(lon2 - lon1);
      const a = Math.sin(dPhi / 2) * Math.sin(dPhi / 2)
        + Math.cos(phi1) * Math.cos(phi2) * Math.sin(dLambda / 2) * Math.sin(dLambda / 2);
      const clamped = Math.min(1, Math.max(0, a));

      return roadFactor * 2 * earthRadiusKm * Math.asin(Math.sqrt(clamped));
    },

    // Sum of consecutive legs. Null when the kolonne has no route to measure.
    originalRouteKm: function(kolonne) {
      const points = this.originalRoutePoints(kolonne);
      if (points.length < 2) {
        return null;
      }

      let total = 0;
      for (let index = 0; index < points.length - 1; index += 1) {
        total += this.haversineKm(
          points[index].lat,
          points[index].lon,
          points[index + 1].lat,
          points[index + 1].lon
        );
      }

      return total;
    },

    formatMetricDistance: function(distance, fractionDigits) {
      const value = Number(distance);
      const digits = fractionDigits === undefined ? 1 : fractionDigits;
      if (!Number.isFinite(value)) {
        return "—";
      }

      return (
        value.toLocaleString("de-DE", {
          minimumFractionDigits: digits,
          maximumFractionDigits: digits
        }) + " km"
      );
    },

    formatMetricQm: function(totalQm) {
      const value = Number(totalQm);
      if (!Number.isFinite(value)) {
        return "—";
      }

      return value.toLocaleString("de-DE", { maximumFractionDigits: 0 }) + " m²";
    },

    completeMetricQm: function(metrics) {
      if (!metrics) {
        return "—";
      }

      return this.formatMetricQm(metrics.total_qm);
    },

    weekdayColor: function(day) {
      return WEEKDAY_MARKER_COLORS[day];
    },

    weekdayShortLabel: function(day) {
      return this.$moment()
        .isoWeekday(day)
        .format("dd");
    },

    triggerDownload: function(data, date, filePrefix) {
      const blob = new Blob([JSON.stringify(data, null, 2)], {
        type: "application/json"
      });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = `${filePrefix || "project-planning-export"}-${date}.json`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    }
  }
};
</script>
